import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Progress,
  Tabs,
  List,
  Avatar,
  DatePicker,
  Select,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Badge,
  InputNumber,
  Divider,
  Checkbox,
  Dropdown,
  Menu,
  ConfigProvider,
  theme
} from 'antd';
import { DownOutlined, CaretDownOutlined } from '@ant-design/icons';
import {
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  CalculatorOutlined,
  PieChartOutlined,
  BarChartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CreditCardOutlined,
  MoneyCollectOutlined,
  AccountBookOutlined,
  SnippetsOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CalendarOutlined,
  BookOutlined,
  ShoppingOutlined,
  WalletOutlined,
  ReconciliationOutlined,
  SettingOutlined,
  AppstoreOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  RollbackOutlined
} from '@ant-design/icons';

// Import our new accounting components
import JournalEntryManagement from '../components/JournalEntry';
import InvoiceManagement from '../components/InvoiceManagement';
import PaymentManagement from '../components/PaymentManagement';
import InvoiceManagementNew from '../components/InvoiceManagement/InvoiceManagementNew';
import PaymentManagementNew from '../components/PaymentManagement/PaymentManagementNew';
import InvoiceItemTemplateList from '../components/InvoiceItemTemplateManagement/InvoiceItemTemplateList';
import VendorBillList from '../components/VendorBillManagement/VendorBillList';
import VendorList from '../components/VendorManagement/VendorList';
import BankReconciliation from '../components/BankReconciliation';
import AccountingSettings from '../components/AccountingSettings';
import JournalEntries from '../components/JournalEntries';
// The Accounting Overview GL tab now renders the by-account grouped report
// from FinancialReports so opening balances + per-account totals are visible
// and the closing balances tie to Trial Balance / Balance Sheet / Income
// Statement (all four reports share calculateBalance + the same date-filter).
// The flat per-line component still exists at '../components/GeneralLedger'
// if a transaction-listing view is needed in future.
import GeneralLedgerReport from '../components/FinancialReports/GeneralLedgerReport';
import APAgingReport from '../components/APAgingReport';
import VendorStatement from '../components/VendorStatement';
import RecurringVendorBillManagement from '../components/RecurringVendorBillManagement';
import ARAgingReport from '../components/ARAgingReport';
import CustomerStatement from '../components/CustomerStatement';
import QuotationManagement from '../components/QuotationManagement';
import CreditNoteManagement from '../components/CreditNoteManagement';
import FinancialReports from '../components/FinancialReports';
import AdvancedFeatures from '../components/AdvancedFeatures';
import JournalManagement from '../components/JournalManagement';
import DeferredRevenueManagement from '../components/DeferredRevenue';
import DeferredExpenseManagement from '../components/DeferredExpense';
import CompanySwitcher from '@/components/CompanySwitcher';
import type { ColumnsType } from 'antd/es/table';
import accountService, { type Account as ApiAccount } from '@/services/accountService';
import journalService, { type Journal } from '@/services/journalService';
import invoiceService, { type InvoiceStats } from '@/services/invoiceService';
import vendorBillService, { type VendorBillStats, type VendorBill } from '@/services/vendorBillService';
import { type Invoice } from '@/services/invoiceService';
import { useAuthStore } from '@/stores/authStore';
import { filterAccountingTabs, canSeeAccountingTab, landingTabFor } from '@/config/modulePermissions';
import accountingInitService, { type InitializationStatus } from '@/services/accountingInitService';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
  parentId?: string;
  balance?: number;
  deprecated: boolean;
  reconcile: boolean;
  note?: string;
  companyId: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Transaction {
  id: string;
  date: string;
  reference: string;
  description: string;
  entries: JournalEntry[];
  totalDebit: number;
  totalCredit: number;
  status: 'draft' | 'posted' | 'cancelled';
  createdBy: string;
}

interface JournalEntry {
  id: string;
  accountId: string;
  accountName: string;
  debit: number;
  credit: number;
  description?: string;
}

interface FinancialData {
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  totalAssets: number;
  totalLiabilities: number;
  equity: number;
  cashFlow: number;
  accountsReceivable: number;
  accountsPayable: number;
  accounts: Account[];
  recentTransactions: Transaction[];
  profitLossData: Array<{
    month: string;
    revenue: number;
    expenses: number;
    profit: number;
  }>;
}

// Tab to route mapping
const TAB_ROUTES: Record<string, string> = {
  'overview': '/accounting',
  'quotations': '/accounting/quotations',
  'invoices': '/accounting/invoices',
  'credit-notes': '/accounting/credit-notes',
  'invoice-templates': '/accounting/invoice-templates',
  'ar-aging': '/accounting/ar-aging',
  'customer-statement': '/accounting/customer-statement',
  'vendors': '/accounting/vendors',
  'vendor-bills': '/accounting/vendor-bills',
  'recurring-bills': '/accounting/recurring-bills',
  'ap-aging': '/accounting/ap-aging',
  'vendor-statement': '/accounting/vendor-statement',
  'accounts': '/accounting/chart-of-accounts',
  'journals': '/accounting/journals',
  'journal': '/accounting/journal-entries',
  'general-ledger': '/accounting/general-ledger',
  'payments': '/accounting/payments',
  'bank-reconciliation': '/accounting/bank-reconciliation',
  'deferred-revenue': '/accounting/deferred-revenue',
  'deferred-expense': '/accounting/deferred-expense',
  'reports': '/accounting/reports',
  'advanced-features': '/accounting/advanced',
  'accounting-settings': '/accounting/settings'
};

// Route to tab mapping (reverse of above)
const ROUTE_TABS: Record<string, string> = Object.entries(TAB_ROUTES).reduce(
  (acc, [tab, route]) => ({ ...acc, [route]: tab }),
  {}
);

const AccountingOverview: React.FC = () => {
    const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Function to get active tab from route
  const getActiveTabFromRoute = useCallback(() => {
    const path = location.pathname;
    // Exact match first
    if (ROUTE_TABS[path]) return ROUTE_TABS[path];
    // Partial match for nested routes
    for (const [route, tab] of Object.entries(ROUTE_TABS)) {
      if (route !== '/accounting' && path.startsWith(route)) return tab;
    }
    // Default to overview
    return 'overview';
  }, [location.pathname]);

  // Navigate to tab
  const navigateToTab = useCallback((tabKey: string) => {
    const route = TAB_ROUTES[tabKey] || '/accounting';
    navigate(route);
  }, [navigate]);

  // Hiding a tab is not the same as closing it — /accounting/journal typed into
  // the address bar would still render. Anyone landing on a tab their role does
  // not hold is moved to the first one it does. The API refuses the data either
  // way; this keeps them off a screen that would only show errors.
  useEffect(() => {
    const tab = getActiveTabFromRoute();
    if (!canSeeAccountingTab(user?.role, tab)) {
      navigate(TAB_ROUTES[landingTabFor(user?.role)] || '/accounting', { replace: true });
    }
  }, [getActiveTabFromRoute, user?.role, navigate]);

  const [financialData, setFinancialData] = useState<FinancialData>({
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    totalAssets: 0,
    totalLiabilities: 0,
    equity: 0,
    cashFlow: 0,
    accountsReceivable: 0,
    accountsPayable: 0,
    accounts: [],
    recentTransactions: [],
    profitLossData: []
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(getActiveTabFromRoute());
  const [transactionModalVisible, setTransactionModalVisible] = useState(false);
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewDetailsModalVisible, setViewDetailsModalVisible] = useState(false);
  const [viewingAccount, setViewingAccount] = useState<Account | null>(null);
  const [journals, setJournals] = useState<Journal[]>([]);
  const [invoiceStats, setInvoiceStats] = useState<InvoiceStats | null>(null);
  const [vendorBillStats, setVendorBillStats] = useState<VendorBillStats | null>(null);
  const [initStatus, setInitStatus] = useState<InitializationStatus | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [recentVendorBills, setRecentVendorBills] = useState<VendorBill[]>([]);

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [location.pathname]);

  // Fetch accounts from API
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const accounts = await accountService.getAccounts();

      // Transform API data to match component interface
      const transformedAccounts: Account[] = accounts.map((acc: any) => ({
        id: acc.id,
        code: acc.code,
        name: acc.name,
        accountType: acc.accountType,
        parentId: acc.parentId,
        balance: Number(acc.balance || 0), // Balance is now included in API response
        deprecated: acc.deprecated,
        reconcile: acc.reconcile,
        note: acc.note,
        companyId: acc.companyId,
        createdAt: acc.createdAt,
        updatedAt: acc.updatedAt
      }));

      // Calculate financial metrics from accounts
      let totalAssets = 0;
      let totalLiabilities = 0;
      let totalEquity = 0;
      let totalRevenue = 0;
      let totalExpenses = 0;
      let cashFlow = 0;
      let accountsReceivable = 0;
      let accountsPayable = 0;

      transformedAccounts.forEach((acc) => {
        const balance = acc.balance || 0;
        const type = acc.accountType?.toUpperCase();
        const code = acc.code?.toLowerCase() || '';
        const name = acc.name?.toLowerCase() || '';

        switch (type) {
          case 'ASSET':
            totalAssets += balance;
            // Check for cash accounts (typically 1000-1099 or contain 'cash', 'bank')
            if (code.startsWith('10') || name.includes('cash') || name.includes('bank')) {
              cashFlow += balance;
            }
            // Check for A/R accounts (typically 1100-1199 or contain 'receivable')
            if (code.startsWith('11') || code.startsWith('12') || name.includes('receivable')) {
              accountsReceivable += balance;
            }
            break;
          case 'LIABILITY':
            totalLiabilities += balance;
            // Check for A/P accounts (typically 2000-2099 or contain 'payable')
            if (code.startsWith('20') || code.startsWith('21') || name.includes('payable')) {
              accountsPayable += balance;
            }
            break;
          case 'EQUITY':
            totalEquity += balance;
            break;
          case 'REVENUE':
            totalRevenue += balance;
            break;
          case 'EXPENSE':
            totalExpenses += balance;
            break;
        }
      });

      const netIncome = totalRevenue - totalExpenses;

      setFinancialData(prev => ({
        ...prev,
        accounts: transformedAccounts,
        totalAssets,
        totalLiabilities,
        equity: totalEquity,
        totalRevenue,
        totalExpenses,
        netIncome,
        cashFlow,
        accountsReceivable,
        accountsPayable
      }));
    } catch (error: any) {
      message.error(error.message || t('Failed to fetch accounts'));
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch journals from API (only active journals)
  const fetchJournals = async () => {
    try {
      const journalsData = await journalService.getActiveJournals();
      setJournals(journalsData);
    } catch (error: any) {
      message.error(error.message || t('Failed to fetch journals'));
    }
  };

  // Fetch recent transactions (journal entries)
  const fetchRecentTransactions = async () => {
    try {
      const entries = await journalService.getJournalEntries();

      // Transform journal entries to Transaction format
      // Sort by date descending and take the most recent 10
      const sortedEntries = entries
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10);

      const transactions: Transaction[] = sortedEntries.map(entry => {
        const totalDebit = entry.lines?.reduce((sum, line) => sum + Number(line.debit || 0), 0) || 0;
        const totalCredit = entry.lines?.reduce((sum, line) => sum + Number(line.credit || 0), 0) || 0;

        return {
          id: entry.id,
          date: new Date(entry.date).toLocaleDateString(),
          reference: entry.name || '-',
          description: entry.narration || entry.ref || `${entry.journal?.name || 'Journal'} Entry`,
          entries: entry.lines?.map(line => ({
            id: line.id || '',
            accountId: line.accountId,
            accountName: line.account?.name || line.name || 'Unknown Account',
            debit: Number(line.debit || 0),
            credit: Number(line.credit || 0),
            description: line.name
          })) || [],
          totalDebit,
          totalCredit,
          status: entry.state.toLowerCase() as 'draft' | 'posted' | 'cancelled',
          createdBy: entry.createdBy || 'System'
        };
      });

      setFinancialData(prev => ({
        ...prev,
        recentTransactions: transactions
      }));
    } catch (error: any) {
      console.error('Failed to fetch recent transactions:', error);
      // Don't show error message for this, just log it
    }
  };

  // Fetch invoice stats
  const fetchInvoiceStats = async () => {
    try {
      const stats = await invoiceService.getInvoiceStats();
      setInvoiceStats(stats);
    } catch (error: any) {
      console.error('Failed to fetch invoice stats:', error);
    }
  };

  // Fetch vendor bill stats
  const fetchVendorBillStats = async () => {
    try {
      const stats = await vendorBillService.getStats();
      setVendorBillStats(stats);
    } catch (error: any) {
      console.error('Failed to fetch vendor bill stats:', error);
    }
  };

  // Fetch recent invoices
  const fetchRecentInvoices = async () => {
    try {
      const invoices = await invoiceService.getAllInvoices({});
      // Sort by date descending and take the most recent 5
      const sorted = invoices
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);
      setRecentInvoices(sorted);
    } catch (error: any) {
      console.error('Failed to fetch recent invoices:', error);
    }
  };

  // Fetch recent vendor bills
  const fetchRecentVendorBills = async () => {
    try {
      const bills = await vendorBillService.getAllBills({});
      // Sort by date descending and take the most recent 5
      const sorted = bills
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5);
      setRecentVendorBills(sorted);
    } catch (error: any) {
      console.error('Failed to fetch recent vendor bills:', error);
    }
  };

  // Auto-initialize accounting module for new companies
  useEffect(() => {
    const checkAndInitialize = async () => {
      // Only run initialization check on overview tab to avoid blocking other tabs
      if (getActiveTabFromRoute() !== 'overview') {
        setIsInitializing(false);
        return;
      }

      try {
        setIsInitializing(true);
        // Add timeout to prevent blocking if API is slow
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Initialization timeout')), 10000)
        );
        const result = await Promise.race([
          accountingInitService.autoInitialize(),
          timeoutPromise
        ]) as any;
        setInitStatus(result.status);

        if (result.wasInitialized) {
          message.success(t('Accounting module initialized! Created {{accountsCreated}} accounts and {{journalsCreated}} journals.', { accountsCreated: result.result?.accountsCreated, journalsCreated: result.result?.journalsCreated }));
        }
      } catch (error: any) {
        console.error('Failed to check/initialize accounting:', error);
        // Don't show error message - just continue with normal loading
      } finally {
        setIsInitializing(false);
      }
    };

    checkAndInitialize();
  }, []);

  useEffect(() => {
    if (!isInitializing) {
      // Always fetch accounts (needed by Chart of Accounts, General Ledger, Journal Entries, etc.)
      fetchAccounts();

      if (activeTab === 'overview') {
        fetchJournals();
        fetchRecentTransactions();
        fetchInvoiceStats();
        fetchVendorBillStats();
        fetchRecentInvoices();
        fetchRecentVendorBills();
      }
    }
  }, [isInitializing, activeTab]);

  // Refresh journals when switching to journal or journal entry tabs
  useEffect(() => {
    if (activeTab === 'journal' || activeTab === 'journals') {
      fetchJournals();
    }
  }, [activeTab]);

  const getAccountTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      ASSET: 'green',
      LIABILITY: 'red',
      EQUITY: 'blue',
      REVENUE: 'purple',
      EXPENSE: 'orange',
      // Lowercase variants for backward compatibility
      asset: 'green',
      liability: 'red',
      equity: 'blue',
      revenue: 'purple',
      expense: 'orange'
    };
    return colors[type] || 'default';
  };

  // Handle creating/updating accounts
  const handleAccountSubmit = async (values: any) => {
    if (!user?.companyId) {
      message.error(t('Company ID not found. Please log in again.'));
      return;
    }

    setLoading(true);
    try {
      if (selectedAccount) {
        // Update existing account
        await accountService.updateAccount(selectedAccount.id, values);
        message.success(t('Account updated successfully'));
      } else {
        // Create new account
        await accountService.createAccount({
          ...values,
          companyId: user.companyId
        });
        message.success(t('Account created successfully'));
      }
      setAccountModalVisible(false);
      form.resetFields();
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error: any) {
      message.error(error.message || t('Failed to save account'));
    } finally {
      setLoading(false);
    }
  };

  // Handle account edit
  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    form.setFieldsValue({
      code: account.code,
      name: account.name,
      accountType: account.accountType,
      parentId: account.parentId,
      reconcile: account.reconcile,
      note: account.note
    });
    setAccountModalVisible(true);
  };

  // Handle account delete
  const handleDeleteAccount = async (accountId: string) => {
    try {
      await accountService.deprecateAccount(accountId);
      message.success(t('Account deprecated successfully'));
      fetchAccounts();
    } catch (error: any) {
      message.error(error.message || t('Failed to deprecate account'));
    }
  };

  // Handle view account details
  const handleViewDetails = (account: Account) => {
    setViewingAccount(account);
    setViewDetailsModalVisible(true);
  };

  // Filter accounts based on search query and type filter
  const filteredAccounts = financialData.accounts.filter(account => {
    // Filter by type
    if (accountFilter !== 'all') {
      if (account.accountType.toLowerCase() !== accountFilter.toLowerCase()) {
        return false;
      }
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesCode = account.code.toLowerCase().includes(query);
      const matchesName = account.name.toLowerCase().includes(query);
      return matchesCode || matchesName;
    }

    return true;
  });

  const handleExportAccounts = () => {
    if (filteredAccounts.length === 0) {
      message.warning(t('No accounts to export'));
      return;
    }
    const rows = filteredAccounts.map(a => ({
      'Code': a.code || '',
      'Name': a.name || '',
      'Type': a.accountType || '',
      'Balance': Number(a.balance || 0),
      'Status': a.isActive !== false ? 'Active' : 'Inactive',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Chart of Accounts');
    XLSX.writeFile(wb, `Chart_of_Accounts_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success(t('Exported {{length}} accounts', { length: filteredAccounts.length }));
  };

  const getTransactionStatusColor = (status: string) => {
    const colors = {
      draft: 'default',
      posted: 'green',
      cancelled: 'red'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  // Invoice status helpers
  const getInvoiceStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      SENT: 'processing',
      PAID: 'success',
      OVERDUE: 'error',
      CANCELLED: 'default'
    };
    return colors[status] || 'default';
  };

  // Vendor bill status helpers
  const getBillStateColor = (state: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'default',
      POSTED: 'processing',
      PAID: 'success',
      CANCELLED: 'error'
    };
    return colors[state] || 'default';
  };

  const getBillPaymentStateColor = (paymentState: string) => {
    const colors: Record<string, string> = {
      NOT_PAID: 'error',
      PARTIAL: 'warning',
      PAID: 'success'
    };
    return colors[paymentState] || 'default';
  };

  const accountColumns: ColumnsType<Account> = [
    {
      title: t('Account Code'),
      dataIndex: 'code',
      key: 'code',
      render: (code) => <Text strong>{code}</Text>
    },
    {
      title: t('Account Name'),
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text>{name}</Text>
    },
    {
      title: t('Type'),
      dataIndex: 'accountType',
      key: 'accountType',
      render: (accountType) => (
        <Tag color={getAccountTypeColor(accountType)}>
          {accountType}
        </Tag>
      )
    },
    {
      title: t('Balance'),
      dataIndex: 'balance',
      key: 'balance',
      render: (balance = 0, record) => (
        <Text strong style={{
          color: record.accountType === 'LIABILITY' || record.accountType === 'EXPENSE' ?
                 (balance > 0 ? '#ff4d4f' : '#52c41a') :
                 (balance > 0 ? '#52c41a' : '#ff4d4f')
        }}>
          ${Math.abs(balance).toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => (a.balance || 0) - (b.balance || 0)
    },
    {
      title: t('Status'),
      dataIndex: 'deprecated',
      key: 'deprecated',
      render: (deprecated) => (
        <Tag color={!deprecated ? 'green' : 'red'}>
          {!deprecated ? 'ACTIVE' : 'DEPRECATED'}
        </Tag>
      )
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title={t('View Details')}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title={t('Edit Account')}>
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditAccount(record)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const transactionColumns: ColumnsType<Transaction> = [
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    },
    {
      title: t('Reference'),
      dataIndex: 'reference',
      key: 'reference',
      render: (reference) => <Text strong>{reference}</Text>
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: t('Debit'),
      dataIndex: 'totalDebit',
      key: 'totalDebit',
      render: (amount) => (
        <Text style={{ color: '#52c41a' }}>
          ${amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.totalDebit - b.totalDebit
    },
    {
      title: t('Credit'),
      dataIndex: 'totalCredit',
      key: 'totalCredit',
      render: (amount) => (
        <Text style={{ color: '#ff4d4f' }}>
          ${amount.toLocaleString()}
        </Text>
      ),
      sorter: (a, b) => a.totalCredit - b.totalCredit
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getTransactionStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: t('Created By'),
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: t('Actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small" />
          <Button type="link" icon={<EditOutlined />} size="small" disabled={record.status === 'posted'} />
          <Button type="link" icon={<PrinterOutlined />} size="small" />
        </Space>
      )
    }
  ];

  // Recent invoices columns (simplified for overview)
  const recentInvoiceColumns: ColumnsType<Invoice> = [
    {
      title: t('Invoice No'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 120,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('Customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 150,
      ellipsis: true,
      render: (text, record) => text || record.customerId || '-'
    },
    {
      title: t('Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
      align: 'right',
      render: (amount) => (
        <Text strong>${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: t('Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 100,
      render: (date) => date ? dayjs(date).format('MMM D') : '-'
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => (
        <Tag color={getInvoiceStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigateToTab('invoices')}
        />
      )
    }
  ];

  // Recent vendor bills columns (simplified for overview)
  const recentVendorBillColumns: ColumnsType<VendorBill> = [
    {
      title: t('Bill No'),
      dataIndex: 'billNo',
      key: 'billNo',
      width: 120,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: t('Vendor'),
      dataIndex: 'vendor',
      key: 'vendor',
      width: 150,
      ellipsis: true,
      render: (vendor) => vendor?.name || '-'
    },
    {
      title: t('Amount'),
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      width: 100,
      align: 'right',
      render: (amount) => (
        <Text strong>${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
      )
    },
    {
      title: t('Due Date'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 100,
      render: (date) => date ? dayjs(date).format('MMM D') : '-'
    },
    {
      title: t('State'),
      dataIndex: 'state',
      key: 'state',
      width: 80,
      render: (state) => (
        <Tag color={getBillStateColor(state)}>{state}</Tag>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigateToTab('vendor-bills')}
        />
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Custom styles for menu visibility */}
      <style>
        {`
          .accounting-nav-menu.ant-menu-horizontal {
            background-color: #fff !important;
          }
          .accounting-nav-menu .ant-menu-item,
          .accounting-nav-menu .ant-menu-submenu-title {
            color: rgba(0, 0, 0, 0.88) !important;
          }
          .accounting-nav-menu .ant-menu-item:hover,
          .accounting-nav-menu .ant-menu-submenu:hover .ant-menu-submenu-title {
            color: #1677ff !important;
          }
          .accounting-nav-menu .ant-menu-item-selected {
            color: #1677ff !important;
          }
          .accounting-nav-menu .ant-menu-item .anticon,
          .accounting-nav-menu .ant-menu-submenu-title .anticon {
            color: inherit !important;
          }
        `}
      </style>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <CalculatorOutlined style={{ marginRight: 8 }} />
            {t('Accounting Overview')}
                                </Title>
        </Col>
        <Col>
          <CompanySwitcher />
        </Col>
      </Row>

      {/* Navigation Menu with Dropdowns */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
        <ConfigProvider
          theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
              colorText: 'rgba(0, 0, 0, 0.88)',
              colorBgContainer: '#ffffff',
            },
            components: {
              Menu: {
                horizontalItemSelectedColor: '#1677ff',
                horizontalItemHoverColor: '#1677ff',
                itemColor: 'rgba(0, 0, 0, 0.88)',
                itemHoverColor: '#1677ff',
                itemSelectedColor: '#1677ff',
                itemBg: '#ffffff',
                subMenuItemBg: '#ffffff',
              }
            }
          }}
        >
          <Menu
            mode="horizontal"
            selectedKeys={[activeTab]}
            onClick={({ key }) => navigateToTab(key)}
            style={{
              borderBottom: 'none',
              backgroundColor: '#ffffff',
              lineHeight: '46px'
            }}
            className="accounting-nav-menu"
            items={filterAccountingTabs(user?.role, [
            {
              key: 'overview',
              icon: <PieChartOutlined />,
              label: t('Overview')
            },
            {
              key: 'receivables',
              icon: <ArrowUpOutlined />,
              label: <span>{t('Receivables (AR)')} <CaretDownOutlined style={{ fontSize: 10, marginLeft: 4 }} /></span>,
              children: [
                { key: 'quotations', icon: <FileTextOutlined />, label: t('Quotations') },
                { key: 'invoices', icon: <FileTextOutlined />, label: t('Invoices') },
                { key: 'credit-notes', icon: <RollbackOutlined />, label: t('Credit AR') },
                { key: 'invoice-templates', icon: <AppstoreOutlined />, label: t('Invoice Templates') },
                { key: 'ar-aging', icon: <ClockCircleOutlined />, label: t('AR Aging Report') },
                { key: 'customer-statement', icon: <SnippetsOutlined />, label: t('Customer Statement') }
              ]
            },
            {
              key: 'payables',
              icon: <ArrowDownOutlined />,
              label: <span>{t('Payables (AP)')} <CaretDownOutlined style={{ fontSize: 10, marginLeft: 4 }} /></span>,
              children: [
                { key: 'vendors', icon: <BankOutlined />, label: t('Vendors') },
                { key: 'vendor-bills', icon: <ShoppingOutlined />, label: t('Vendor Bills') },
                { key: 'recurring-bills', icon: <SyncOutlined />, label: t('Recurring Bills') },
                { key: 'ap-aging', icon: <ClockCircleOutlined />, label: t('AP Aging Report') },
                { key: 'vendor-statement', icon: <SnippetsOutlined />, label: t('Vendor Statement') }
              ]
            },
            {
              key: 'ledger',
              icon: <AccountBookOutlined />,
              label: <span>{t('Ledger')} <CaretDownOutlined style={{ fontSize: 10, marginLeft: 4 }} /></span>,
              children: [
                { key: 'accounts', icon: <CalculatorOutlined />, label: t('Chart of Accounts') },
                { key: 'journals', icon: <BookOutlined />, label: t('Journals') },
                { key: 'journal', icon: <SnippetsOutlined />, label: t('Journal Entries') },
                { key: 'general-ledger', icon: <AccountBookOutlined />, label: t('General Ledger') }
              ]
            },
            {
              key: 'banking',
              icon: <WalletOutlined />,
              label: <span>{t('Banking')} <CaretDownOutlined style={{ fontSize: 10, marginLeft: 4 }} /></span>,
              children: [
                { key: 'payments', icon: <MoneyCollectOutlined />, label: t('Payments') },
                { key: 'bank-reconciliation', icon: <ReconciliationOutlined />, label: t('Bank Reconciliation') }
              ]
            },
            {
              key: 'reports',
              icon: <BarChartOutlined />,
              label: t('Reports')
            },
            {
              key: 'advanced-features',
              icon: <AppstoreOutlined />,
              label: <span>{t('Advanced')} <CaretDownOutlined style={{ fontSize: 10, marginLeft: 4 }} /></span>,
              children: [
                { key: 'advanced-features', icon: <AppstoreOutlined />, label: t('Advanced Features') },
                { key: 'deferred-revenue', icon: <ClockCircleOutlined />, label: t('Deferred Revenue') },
                { key: 'deferred-expense', icon: <ClockCircleOutlined />, label: t('Deferred Expense') }
              ]
            },
            {
              key: 'accounting-settings',
              icon: <SettingOutlined />,
              label: t('Settings')
            }
          ])}
          />
        </ConfigProvider>
      </Card>

      {/* Content Area */}
      <div style={{ marginTop: 16 }}>
        {/* Overview */}
        {activeTab === 'overview' && (
          <>
            {/* Quick Actions Bar */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Text strong style={{ fontSize: 14 }}>{t('Quick Actions')}</Text>
                </Col>
                <Col>
                  <Space size="middle">
                    <Button type="primary" icon={<FileTextOutlined />} onClick={() => navigateToTab('invoices')}>
                      {t('New Invoice')}
                                                              </Button>
                    <Button icon={<ShoppingOutlined />} onClick={() => navigateToTab('vendor-bills')}>
                      {t('New Bill')}
                                                              </Button>
                    <Button icon={<MoneyCollectOutlined />} onClick={() => navigateToTab('payments')}>
                      {t('Record Payment')}
                                                              </Button>
                    <Button icon={<BookOutlined />} onClick={() => navigateToTab('journal')}>
                      {t('Journal Entry')}
                                                              </Button>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Action Items / Alerts */}
            {((invoiceStats?.overdueInvoices || 0) > 0 || (vendorBillStats?.overdueBills || 0) > 0 || (invoiceStats?.draftInvoices || 0) > 0 || (vendorBillStats?.draftBills || 0) > 0) && (
              <Card
                size="small"
                style={{ marginBottom: 16, borderLeft: '4px solid #faad14' }}
                bodyStyle={{ padding: '12px 16px' }}
              >
                <Row gutter={24} align="middle">
                  <Col>
                    <ClockCircleOutlined style={{ fontSize: 20, color: '#faad14' }} />
                  </Col>
                  <Col flex="auto">
                    <Space size="large" wrap>
                      {(invoiceStats?.overdueInvoices || 0) > 0 && (
                        <Button type="link" danger size="small" onClick={() => navigateToTab('invoices')} style={{ padding: 0 }}>
                          <Badge count={invoiceStats?.overdueInvoices} size="small" offset={[8, 0]}>
                            <Text type="danger">{invoiceStats?.overdueInvoices} overdue invoice(s)</Text>
                          </Badge>
                        </Button>
                      )}
                      {(vendorBillStats?.overdueBills || 0) > 0 && (
                        <Button type="link" danger size="small" onClick={() => navigateToTab('vendor-bills')} style={{ padding: 0 }}>
                          <Badge count={vendorBillStats?.overdueBills} size="small" offset={[8, 0]}>
                            <Text type="danger">{vendorBillStats?.overdueBills} overdue bill(s)</Text>
                          </Badge>
                        </Button>
                      )}
                      {(invoiceStats?.draftInvoices || 0) > 0 && (
                        <Button type="link" size="small" onClick={() => navigateToTab('invoices')} style={{ padding: 0 }}>
                          <Text type="secondary">{invoiceStats?.draftInvoices} draft invoice(s) pending</Text>
                        </Button>
                      )}
                      {(vendorBillStats?.draftBills || 0) > 0 && (
                        <Button type="link" size="small" onClick={() => navigateToTab('vendor-bills')} style={{ padding: 0 }}>
                          <Text type="secondary">{vendorBillStats?.draftBills} draft bill(s) pending</Text>
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Key Financial Metrics - Compact Row */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" hoverable onClick={() => navigateToTab('invoices')} style={{ cursor: 'pointer' }}>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>{t('A/R Outstanding')}</Text>}
                    value={invoiceStats?.outstandingAmount || 0}
                    prefix="$"
                    precision={2}
                    valueStyle={{ color: '#52c41a', fontSize: 20 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {invoiceStats?.sentInvoices || 0} unpaid invoices
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" hoverable onClick={() => navigateToTab('vendor-bills')} style={{ cursor: 'pointer' }}>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>{t('A/P Outstanding')}</Text>}
                    value={vendorBillStats?.totalAmountDue || 0}
                    prefix="$"
                    precision={2}
                    valueStyle={{ color: '#ff4d4f', fontSize: 20 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {vendorBillStats?.postedBills || 0} unpaid bills
                    </Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" hoverable onClick={() => navigateToTab('accounts')} style={{ cursor: 'pointer' }}>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>{t('Cash Position')}</Text>}
                    value={financialData.cashFlow}
                    prefix="$"
                    precision={0}
                    valueStyle={{ color: '#1890ff', fontSize: 20 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t('Bank & Cash accounts')}
                                                              </Text>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" hoverable onClick={() => navigateToTab('reports')} style={{ cursor: 'pointer' }}>
                  <Statistic
                    title={<Text type="secondary" style={{ fontSize: 12 }}>{t('Net Income (YTD)')}</Text>}
                    value={financialData.netIncome}
                    prefix={financialData.netIncome >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    precision={0}
                    valueStyle={{ color: financialData.netIncome >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 20 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {t('Revenue - Expenses')}
                                                              </Text>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Main Content - Two Column Layout */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              {/* Receivables Card */}
              <Col xs={24} lg={12}>
                <Card
                  title={<><ArrowUpOutlined style={{ color: '#52c41a', marginRight: 8 }} />{t('Accounts Receivable')}</>}
                  extra={<Button type="link" size="small" onClick={() => navigateToTab('ar-aging')}>{t('Aging Report')}</Button>}
                  size="small"
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title={t('Total Outstanding')}
                        value={invoiceStats?.outstandingAmount || 0}
                        prefix="$"
                        precision={2}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t('Overdue Amount')}
                        value={(invoiceStats?.outstandingAmount || 0) * ((invoiceStats?.overdueInvoices || 0) / Math.max(invoiceStats?.sentInvoices || 1, 1))}
                        prefix="$"
                        precision={2}
                        valueStyle={{ fontSize: 18, color: (invoiceStats?.overdueInvoices || 0) > 0 ? '#ff4d4f' : undefined }}
                      />
                    </Col>
                  </Row>
                  <Divider style={{ margin: '12px 0' }} />
                  <Row gutter={8}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Draft')}</Text>
                        <div><Text strong>{invoiceStats?.draftInvoices || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Sent')}</Text>
                        <div><Text strong>{invoiceStats?.sentInvoices || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Paid')}</Text>
                        <div><Text strong style={{ color: '#52c41a' }}>{invoiceStats?.paidInvoices || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Overdue')}</Text>
                        <div><Text strong style={{ color: '#ff4d4f' }}>{invoiceStats?.overdueInvoices || 0}</Text></div>
                      </div>
                    </Col>
                  </Row>
                  <div style={{ marginTop: 12 }}>
                    <Button type="primary" size="small" block onClick={() => navigateToTab('invoices')}>
                      {t('View All Invoices')}
                                                              </Button>
                  </div>
                </Card>
              </Col>

              {/* Payables Card */}
              <Col xs={24} lg={12}>
                <Card
                  title={<><ArrowDownOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />{t('Accounts Payable')}</>}
                  extra={<Button type="link" size="small" onClick={() => navigateToTab('ap-aging')}>{t('Aging Report')}</Button>}
                  size="small"
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title={t('Total Outstanding')}
                        value={vendorBillStats?.totalAmountDue || 0}
                        prefix="$"
                        precision={2}
                        valueStyle={{ fontSize: 18 }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={t('Overdue Amount')}
                        value={vendorBillStats?.totalAmountOverdue || 0}
                        prefix="$"
                        precision={2}
                        valueStyle={{ fontSize: 18, color: (vendorBillStats?.overdueBills || 0) > 0 ? '#ff4d4f' : undefined }}
                      />
                    </Col>
                  </Row>
                  <Divider style={{ margin: '12px 0' }} />
                  <Row gutter={8}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Draft')}</Text>
                        <div><Text strong>{vendorBillStats?.draftBills || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Posted')}</Text>
                        <div><Text strong>{vendorBillStats?.postedBills || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Paid')}</Text>
                        <div><Text strong style={{ color: '#52c41a' }}>{vendorBillStats?.paidBills || 0}</Text></div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{t('Overdue')}</Text>
                        <div><Text strong style={{ color: '#ff4d4f' }}>{vendorBillStats?.overdueBills || 0}</Text></div>
                      </div>
                    </Col>
                  </Row>
                  <div style={{ marginTop: 12 }}>
                    <Button type="primary" size="small" block onClick={() => navigateToTab('vendor-bills')}>
                      {t('View All Bills')}
                                                              </Button>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Financial Summary Row */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={16}>
                <Card
                  title={t('Balance Sheet Summary')}
                  size="small"
                  extra={<Button type="link" size="small" onClick={() => navigateToTab('reports')}>{t('Full Report')}</Button>}
                >
                  <Row gutter={24}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Text type="secondary">{t('Total Assets')}</Text>
                        <div>
                          <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                            ${financialData.totalAssets.toLocaleString()}
                          </Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{t('Cash: $')}{financialData.cashFlow.toLocaleString()}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>{t('A/R: $')}{financialData.accountsReceivable.toLocaleString()}</Text>
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Text type="secondary">{t('Total Liabilities')}</Text>
                        <div>
                          <Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>
                            ${financialData.totalLiabilities.toLocaleString()}
                          </Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>{t('A/P: $')}{financialData.accountsPayable.toLocaleString()}</Text>
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Text type="secondary">{t("Owner's Equity")}</Text>
                        <div>
                          <Text strong style={{ fontSize: 20, color: '#1890ff' }}>
                            ${financialData.equity.toLocaleString()}
                          </Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <Progress
                            percent={financialData.totalAssets > 0 ? (financialData.equity / financialData.totalAssets) * 100 : 0}
                            size="small"
                            strokeColor="#1890ff"
                            format={p => `${(p || 0).toFixed(0)}%`}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title={t('P&L Summary')} size="small" extra={<Button type="link" size="small" onClick={() => navigateToTab('reports')}>{t('Details')}</Button>}>
                  <div style={{ marginBottom: 12 }}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">{t('Revenue')}</Text></Col>
                      <Col><Text strong style={{ color: '#52c41a' }}>${financialData.totalRevenue.toLocaleString()}</Text></Col>
                    </Row>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <Row justify="space-between">
                      <Col><Text type="secondary">{t('Expenses')}</Text></Col>
                      <Col><Text strong style={{ color: '#ff4d4f' }}>${financialData.totalExpenses.toLocaleString()}</Text></Col>
                    </Row>
                  </div>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between">
                    <Col><Text strong>{t('Net Income')}</Text></Col>
                    <Col>
                      <Text strong style={{ color: financialData.netIncome >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 16 }}>
                        ${financialData.netIncome.toLocaleString()}
                      </Text>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* Recent Invoices and Bills */}
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <Card
                  title={<><FileTextOutlined style={{ marginRight: 8 }} />{t('Recent Invoices')}</>}
                  size="small"
                  extra={<Button type="link" size="small" onClick={() => navigateToTab('invoices')}>{t('View All')}</Button>}
                >
                  <Table
                    dataSource={recentInvoices}
                    columns={recentInvoiceColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: t('No recent invoices') }}
                    scroll={{ x: 520 }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  title={<><ShoppingOutlined style={{ marginRight: 8 }} />{t('Recent Vendor Bills')}</>}
                  size="small"
                  extra={<Button type="link" size="small" onClick={() => navigateToTab('vendor-bills')}>{t('View All')}</Button>}
                >
                  <Table
                    dataSource={recentVendorBills}
                    columns={recentVendorBillColumns}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: t('No recent vendor bills') }}
                    scroll={{ x: 520 }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Receivables (AR) */}
        {activeTab === 'quotations' && <QuotationManagement />}
        {activeTab === 'invoices' && <InvoiceManagementNew />}
        {activeTab === 'credit-notes' && <CreditNoteManagement />}
        {activeTab === 'invoice-templates' && <InvoiceItemTemplateList />}
        {activeTab === 'ar-aging' && <ARAgingReport />}
        {activeTab === 'customer-statement' && <CustomerStatement />}

        {/* Payables (AP) */}
        {activeTab === 'vendors' && <VendorList />}
        {activeTab === 'vendor-bills' && <VendorBillList />}
        {activeTab === 'recurring-bills' && <RecurringVendorBillManagement />}
        {activeTab === 'ap-aging' && <APAgingReport />}
        {activeTab === 'vendor-statement' && <VendorStatement />}

        {/* Ledger */}
        {activeTab === 'journals' && <JournalManagement />}
        {activeTab === 'accounts' && (
          <Card
            title={t('Chart of Accounts')}
            extra={
              <Space>
                <Button icon={<DownloadOutlined />} onClick={handleExportAccounts}>
                  {t('Export')}
                                        </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedAccount(null);
                    form.resetFields();
                    setAccountModalVisible(true);
                  }}
                >
                  {t('Add Account')}
                                        </Button>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Search
                  placeholder={t('Search accounts by code or name...')}
                  style={{ width: 300 }}
                  allowClear
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={(value) => setSearchQuery(value)}
                />
                <Select
                  placeholder={t('Filter by type')}
                  style={{ width: 150 }}
                  value={accountFilter}
                  onChange={(value) => setAccountFilter(value)}
                >
                  <Select.Option value="all">{t('All Types')}</Select.Option>
                  <Select.Option value="asset">{t('Assets')}</Select.Option>
                  <Select.Option value="liability">{t('Liabilities')}</Select.Option>
                  <Select.Option value="equity">{t('Equity')}</Select.Option>
                  <Select.Option value="revenue">{t('Revenue')}</Select.Option>
                  <Select.Option value="expense">{t('Expenses')}</Select.Option>
                </Select>
              </Space>
            </div>
            <Table
              dataSource={filteredAccounts}
              columns={accountColumns}
              loading={loading}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => t('Total {{total}} accounts', { total })
              }}
              rowKey="id"
            />
          </Card>
        )}
        {activeTab === 'journal' && <JournalEntries journals={journals} accounts={financialData.accounts} />}
        {activeTab === 'general-ledger' && <GeneralLedgerReport />}

        {/* Banking */}
        {activeTab === 'payments' && <PaymentManagementNew />}
        {activeTab === 'bank-reconciliation' && <BankReconciliation />}

        {/* Deferred Revenue & Expense */}
        {activeTab === 'deferred-revenue' && <DeferredRevenueManagement />}
        {activeTab === 'deferred-expense' && <DeferredExpenseManagement />}

        {/* Reports */}
        {activeTab === 'reports' && <FinancialReports />}

        {/* Advanced Features */}
        {activeTab === 'advanced-features' && <AdvancedFeatures />}

        {/* Settings */}
        {activeTab === 'accounting-settings' && <AccountingSettings />}
      </div>

      {/* Account Modal */}
      <Modal
        title={selectedAccount ? t('Edit Account') : t('Create Account')}
        open={accountModalVisible}
        onCancel={() => {
          setAccountModalVisible(false);
          form.resetFields();
          setSelectedAccount(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} onFinish={handleAccountSubmit} layout="vertical">
          <Form.Item
            name="code"
            label={t('Account Code')}
            rules={[{ required: true, message: t('Please enter account code') }]}
          >
            <Input placeholder="e.g., 1000, 2000, 3000" />
          </Form.Item>

          <Form.Item
            name="name"
            label={t('Account Name')}
            rules={[{ required: true, message: t('Please enter account name') }]}
          >
            <Input placeholder="e.g., Assets, Cash, Accounts Receivable" />
          </Form.Item>

          <Form.Item
            name="accountType"
            label={t('Account Type')}
            rules={[{ required: true, message: t('Please select account type') }]}
          >
            <Select placeholder={t('Select account type')}>
              <Select.Option value="ASSET">{t('Asset')}</Select.Option>
              <Select.Option value="LIABILITY">{t('Liability')}</Select.Option>
              <Select.Option value="EQUITY">{t('Equity')}</Select.Option>
              <Select.Option value="REVENUE">{t('Revenue')}</Select.Option>
              <Select.Option value="EXPENSE">{t('Expense')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="parentId"
            label={t('Parent Account')}
            tooltip={t('Select a parent account to create a sub-account')}
          >
            <Select allowClear placeholder={t('Select parent account (optional)')}>
              {financialData.accounts
                .filter(a => !a.deprecated && !a.parentId)
                .map(a => (
                  <Select.Option key={a.id} value={a.id}>
                    {a.code} - {a.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reconcile"
            valuePropName="checked"
            tooltip={t('Enable for bank accounts and accounts that need reconciliation')}
          >
            <Checkbox>{t('Reconcilable (for bank accounts)')}</Checkbox>
          </Form.Item>

          <Form.Item
            name="note"
            label={t('Notes')}
          >
            <Input.TextArea
              rows={3}
              placeholder={t('Additional notes or description for this account')}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        title={t('Account Details')}
        open={viewDetailsModalVisible}
        onCancel={() => {
          setViewDetailsModalVisible(false);
          setViewingAccount(null);
        }}
        footer={[
          <Button key="close" onClick={() => setViewDetailsModalVisible(false)}>
            {t('Close')}
                          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              if (viewingAccount) {
                handleEditAccount(viewingAccount);
                setViewDetailsModalVisible(false);
              }
            }}
          >
            {t('Edit Account')}
                        </Button>
        ]}
        width={600}
      >
        {viewingAccount && (
          <div style={{ padding: '16px 0' }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Account Code')}</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{viewingAccount.code}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Account Name')}</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>{viewingAccount.name}</Text>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Account Type')}</Text>
                  <div>
                    <Tag color={getAccountTypeColor(viewingAccount.accountType)} style={{ fontSize: 14 }}>
                      {viewingAccount.accountType}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Status')}</Text>
                  <div>
                    <Tag color={!viewingAccount.deprecated ? 'green' : 'red'} style={{ fontSize: 14 }}>
                      {!viewingAccount.deprecated ? 'ACTIVE' : 'DEPRECATED'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Reconcilable')}</Text>
                  <div>
                    <Tag color={viewingAccount.reconcile ? 'blue' : 'default'} style={{ fontSize: 14 }}>
                      {viewingAccount.reconcile ? 'YES' : 'NO'}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">{t('Current Balance')}</Text>
                  <div>
                    <Text strong style={{
                      fontSize: 16,
                      color: viewingAccount.accountType === 'LIABILITY' || viewingAccount.accountType === 'EXPENSE' ?
                             ((viewingAccount.balance || 0) > 0 ? '#ff4d4f' : '#52c41a') :
                             ((viewingAccount.balance || 0) > 0 ? '#52c41a' : '#ff4d4f')
                    }}>
                      ${Math.abs(viewingAccount.balance || 0).toLocaleString()}
                    </Text>
                  </div>
                </div>
              </Col>
              {viewingAccount.parentId && (
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">{t('Parent Account')}</Text>
                    <div>
                      <Text>{
                        financialData.accounts.find(a => a.id === viewingAccount.parentId)?.name ||
                        viewingAccount.parentId
                      }</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.note && (
                <Col span={24}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">{t('Notes')}</Text>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: 4,
                      marginTop: 8
                    }}>
                      <Text>{viewingAccount.note}</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.createdAt && (
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">{t('Created At')}</Text>
                    <div>
                      <Text>{new Date(viewingAccount.createdAt).toLocaleString()}</Text>
                    </div>
                  </div>
                </Col>
              )}
              {viewingAccount.updatedAt && (
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">{t('Last Updated')}</Text>
                    <div>
                      <Text>{new Date(viewingAccount.updatedAt).toLocaleString()}</Text>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountingOverview; 